/**
 * Analytics API Client
 * ====================
 * API client for communicating with backend analytics endpoints
 */

import type {
  DashboardStats,
  DashboardFilters,
  ConversationSummary,
  ConversationDetail,
  ConversationsCount,
  ConversationsFilters,
  ConversationsQuery,
  ExportRequest,
  ExportResponse,
  CountryOption,
} from '../types/analytics'
import { autoRefreshToken } from './auth'

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api'

/**
 * Build query string from filters object
 */
function buildQueryString(filters: Record<string, any>): string {
  const params = new URLSearchParams()

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value))
    }
  })

  const queryString = params.toString()
  return queryString ? `?${queryString}` : ''
}

/**
 * Get authentication headers with JWT token
 *
 * Note: Migrated to JWT authentication (Phase 11)
 * Uses access_token from localStorage, auto-refreshes if expired
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  // Get valid JWT token (auto-refresh if expired)
  const accessToken = await autoRefreshToken()

  if (!accessToken) {
    throw new Error('Not authenticated')
  }

  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  }
}

/**
 * Fetch dashboard statistics
 */
export async function fetchDashboard(
  filters: DashboardFilters = {}
): Promise<DashboardStats> {
  const queryString = buildQueryString(filters)
  const headers = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/analytics/dashboard${queryString}`, {
    headers,
  })

  if (!response.ok) {
    throw new Error(`Dashboard fetch failed: ${response.statusText}`)
  }

  return response.json()
}

/**
 * Fetch conversations list
 */
export async function fetchConversations(
  query: ConversationsQuery = {}
): Promise<ConversationSummary[]> {
  const queryString = buildQueryString(query)
  const headers = await getAuthHeaders()
  const response = await fetch(
    `${API_BASE}/analytics/conversations${queryString}`,
    {
      headers,
    }
  )

  if (!response.ok) {
    throw new Error(`Conversations fetch failed: ${response.statusText}`)
  }

  return response.json()
}

/**
 * Count conversations matching the filters, ignoring pagination
 *
 * Drives the page range in the pagination bar and tells the operator how many
 * conversations a filter-wide export will contain.
 */
export async function fetchConversationsCount(
  filters: ConversationsFilters = {}
): Promise<ConversationsCount> {
  const queryString = buildQueryString(filters)
  const headers = await getAuthHeaders()
  const response = await fetch(
    `${API_BASE}/analytics/conversations/count${queryString}`,
    {
      headers,
    }
  )

  if (!response.ok) {
    throw new Error(`Conversations count failed: ${response.statusText}`)
  }

  return response.json()
}

/**
 * Fetch conversation detail by ID
 */
export async function fetchConversationDetail(
  id: number
): Promise<ConversationDetail> {
  const headers = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/analytics/conversations/${id}`, {
    headers,
  })

  if (!response.ok) {
    throw new Error(`Conversation detail fetch failed: ${response.statusText}`)
  }

  return response.json()
}

/**
 * Export conversations to Markdown or CSV
 *
 * Pass `conversation_ids` to export a hand-picked selection, or `filters` to
 * export everything the filter matches regardless of the current page.
 */
export async function exportConversations(
  request: ExportRequest
): Promise<ExportResponse> {
  const headers = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/analytics/export`, {
    method: 'POST',
    headers,
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    // The backend explains refusals (e.g. a filter matching too many
    // conversations) in `detail`; showing statusText alone would hide that.
    let detail = response.statusText
    try {
      const body = await response.json()
      if (body?.detail) {
        detail = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail)
      }
    } catch {
      // Non-JSON error body - keep statusText.
    }
    throw new Error(detail)
  }

  return response.json()
}

/**
 * Health check
 */
export async function checkHealth(): Promise<{ status: string }> {
  const response = await fetch(`${API_BASE}/analytics/health`)

  if (!response.ok) {
    throw new Error(`Health check failed: ${response.statusText}`)
  }

  return response.json()
}

/**
 * Fetch list of countries with conversation data
 */
export async function fetchConversationCountries(
  siteCode?: string
): Promise<CountryOption[]> {
  const queryString = siteCode ? `?site_code=${encodeURIComponent(siteCode)}` : ''
  const headers = await getAuthHeaders()
  const response = await fetch(
    `${API_BASE}/analytics/conversations/countries${queryString}`,
    {
    headers,
  }
  )

  if (!response.ok) {
    throw new Error(`Countries fetch failed: ${response.statusText}`)
  }

  return response.json()
}
